import React, {
    useState, useRef, useEffect,
} from 'react';
import { message, Steps } from 'antd';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
    useStores,
    useEmailOTPFlow,
    useLoginMethodPreference,
    LOGIN_METHOD_EMAIL,
    LOGIN_METHOD_OTP,
} from '@/components/hooks';
import {
    useVerifyAction,
} from '@/components/MiniApps/RecipientPage/InnerContent/LinkView/hooks';
import styles from './OTPAuthSteps.module.scss';
import OTPAuthEmailBox from './OTPAuthEmailBox';
import OTPAuthCodeBox from './OTPAuthCodeBox';
import OTPAuthPasswordBox from './OTPAuthPasswordBox';
import OTPAuthPhoneBox from './OTPAuthPhoneBox';
import { PhoneMissingModal, PasswordMissingModal } from './OTPAuthStepsMessages';
import {
    ALLOW_FORWARDING,
    REQUIRED_PASSWORD_VERIFICATION,
    CURRENT_ORGANIZATION,
    OTP_ADMINS_RESTRICTED,
    OTP_COLLABORATORS_RESTRICTED,
    RECIPIENT_EMAIL_FOR_LOGIN,
} from '@/consts';
import { BASEURL, ENDPOINTS, getFile } from '@/api';
import { isOfficeFile } from '@/components/utils';
import { type CognitoAuthError, type PhoneIdentity, SimpleCallback } from '@/types/types';
import EmailChangeMessage from './OTPAuthStepsMessages/EmailChangeMessage';
import { trySignUp, InvalidLinkError } from '@/components/hooks/useOTPFlow/helpers';
import {
    useFilesActionsContext,
} from '@/components/MiniApps/RecipientPage/InnerContent/LinkView/hooks/useFilesActionContext.ts';
import './index.scss';
import {
    caseInsensitiveUrlParam,
    parseAuthErrorType,
    saveRedirectPath,
    checkMessageContainsPermissions,
    getErrorResponse,
    checkIsNetworkError,
    checkIsMFARequired as checkIsMFARequiredAPI,
    checkIsMFAPasswordRequired as checkIsMFAPasswordRequiredAPI,
} from '@/components/utils';
import { CognitoErrorCodes } from '@/types/types';
import i18n from '@/content';
import { OpenOptions } from '@/config/openOptions.ts';
import Spinner from '@/components/Common/Spin';
import appConfig from '@/config/env';

const { Step } = Steps;
const nameSpace = 'recipientPage';
type CodeSendingMethod = 'phone' | 'voice';

// Guard against duplicate auto-sends of the OTP code triggered by the initial
// `useEffect` running more than once (dep toggle or component remount).
// Lives in module scope so it survives the component's unmount/remount cycle,
// which a `useRef` would not. Only gates *automatic* sends — user-initiated
// sends (Submit / Resend) bypass this guard entirely.
const autoSendGuard = new Map<string, number>();
const AUTO_SEND_GUARD_MS = 15_000;
const shouldSkipAutoSend = (key: string): boolean => {
    if (!key) return false;
    const now = Date.now();
    const last = autoSendGuard.get(key);
    if (last !== undefined && now - last < AUTO_SEND_GUARD_MS) {
        return true;
    }
    autoSendGuard.set(key, now);
    return false;
};
type AdminMFAMethod = CodeSendingMethod | 'password';

interface OTPAuthStepsProps<OpenFileResult> {
    openFile: (code: string) => Promise<OpenFileResult>;
    closeOTP: SimpleCallback;
    stepsMounted: () => void;
    stepsInitialized: boolean;
}

const ADMIN_MESSAGE_DURATION = 30;

// eslint-disable-next-line @typescript-eslint/comma-dangle
const OTPAuthSteps = observer(<OpenFileResult,>({
    openFile, closeOTP, stepsInitialized, stepsMounted,
}: OTPAuthStepsProps<OpenFileResult>) => {
    const [currentStep, setCurrentStep] = useState<number | null>(null);
    const [showPhoneMissingModal, setShowPhoneMissingModal] = useState<boolean>(false);
    const [showPasswordMissingModal, setShowPasswordMissingModal] = useState<boolean>(false);
    const [hasPhone, setHasPhone] = useState<boolean>(false);
    const [hasPassword, setHasPassword] = useState<boolean>(false);
    const [codeError, setCodeError] = useState<boolean>(false);
    const [passwordError, setPasswordError] = useState<boolean>(false);
    const [wrongPasswordAttempts, setWrongPasswordAttempts] = useState<number>(3);
    const [textSource, setTextSource] = useState<'phone' | 'email' | null>(null);
    const [passwordHint, setPasswordHint] = useState<string | null>(null);
    // Track if phone code has been sent (for manual OTP mode when AUTOMATIC_OTP is false)
    const [isPhoneCodeSent, setIsPhoneCodeSent] = useState<boolean>(appConfig.AUTOMATIC_OTP !== false);
    const { t } = useTranslation();

    const newEmail = useRef<string>('');
    const isMFARequired = useRef<boolean>(false);
    const recipientPhone = useRef<PhoneIdentity | null>(null);
    const hasRedirectedToLogin = useRef<boolean>(false);

    const navigate = useNavigate();

    const {
        authSettingsStore,
        recipientPageStore: { sharedFilesStore, emailMessageStore },
        userStore,
    } = useStores();

    const { isAdmin, isCollaborator, isUserSetUp: isUserLoggedIn, currentUserEmail } = userStore;

    const otpAdminsRestricted = typeof window !== 'undefined'
        ? sessionStorage.getItem(OTP_ADMINS_RESTRICTED) === 'true'
        : false;
    const otpCollaboratorsRestricted = typeof window !== 'undefined'
        ? sessionStorage.getItem(OTP_COLLABORATORS_RESTRICTED) === 'true'
        : false;

    const {
        allFilesPoliciesAggregate,
        authRequirements,
        recipientEmail,
        selectedFilePolicyAggregate,
        filesList,
        setIsMFARequired,
        isLoading,
        setIsContentLoading,
        senderEmail,
        allFilesCount,
        isMainDataLoaded,
        updateRequestsMap,
        anyRequestInProcess,
    } = sharedFilesStore;

    const {
        messageId,
    } = emailMessageStore;

    const {
        lastAccessType,
    } = useVerifyAction(sharedFilesStore, authSettingsStore.API);

    const policiesAggregate = selectedFilePolicyAggregate && lastAccessType !== 'batchDownload'
        ? selectedFilePolicyAggregate
        : allFilesPoliciesAggregate;

    const {
        email: hasEmailVerification,
        phone: hasPhoneVerification,
        password: hasPasswordProtection,
    } = authRequirements;
    const hasAllowForwarding = !!policiesAggregate?.[ALLOW_FORWARDING];

    const { selectFile } = useFilesActionsContext();

    const {
        checkAuthCode,
        email,
        emailAuthFirstStepMessages,
        onSendEmailError,
        sendEmailAuthCode,
        sendAuthCode,
        resetEmail,
    } = useEmailOTPFlow(
        t,
        authSettingsStore,
        userStore,
        recipientEmail,
    );
    const startLoading = (): void => {
        setIsContentLoading(true);
    };

    const stopLoading = (): void => {
        setIsContentLoading(false);
    };

    const { getLoginMethodPreference, setLoginMethodPreference, clearLoginMethodPreference } = useLoginMethodPreference();

    const redirectUnauthenticatedToLogin = async (emailForLogin?: string): Promise<void> => {
        // Never redirect a user who is already logged in to the sign-in page —
        // doing so while runSignedInUserFlow is active creates an infinite loop
        // (/shared-file → signIn → /shared-file → signIn → …).
        if (isUserLoggedIn || hasRedirectedToLogin.current) {
            return;
        }
        hasRedirectedToLogin.current = true;

        setLoginMethodPreference(LOGIN_METHOD_EMAIL);
        saveRedirectPath(`${window.location.pathname}${window.location.search}`);
        const resolvedEmail = emailForLogin || newEmail.current || recipientEmail;
        if (resolvedEmail) {
            sessionStorage.setItem(RECIPIENT_EMAIL_FOR_LOGIN, resolvedEmail);
            try {
                await trySignUp(resolvedEmail.toLowerCase(), authSettingsStore.API);
            } catch (error) {
                console.error('Failed to pre-provision Cognito user before login redirect:', error);
            }
        }
        navigate('/signIn');
    };

    const handlePhoneMissing = async (): Promise<void> => {
        if (appConfig.LOGIN_FALLBACK_ON_MISSING_PHONE && !isUserLoggedIn) {
            if (getLoginMethodPreference() === LOGIN_METHOD_OTP) {
                clearLoginMethodPreference();
                setShowPhoneMissingModal(true);
                return;
            }

            await redirectUnauthenticatedToLogin();
        } else {
            setShowPhoneMissingModal(true);
        }
    };

    const isNetworkRestrictionError = (error: unknown): boolean => (
        checkIsNetworkError(error)
        || (error as CognitoAuthError)?.code === CognitoErrorCodes.NetworkError
    );

    const handleNetworkRestriction = (error: unknown): void => {
        sharedFilesStore.setLinkErrorObject(error);
        sharedFilesStore.setErrorType('networkRestriction');
    };

    const checkPhoneAvailability = async (): Promise<void> => {
        updateRequestsMap('checkPhoneAvailability', true);
        try {
            let emailToCheck: string;
            if (isUserLoggedIn && hasEmailVerification && hasPhoneVerification) {
                emailToCheck = currentUserEmail;
            } else {
                emailToCheck = newEmail.current || email || recipientEmail || currentUserEmail;
            }

            const response = await authSettingsStore.API.get(
                BASEURL.backend(),
                ENDPOINTS.getPhoneByEmail(emailToCheck),
                {},
            );
            if (response?.phone && response?.prefix) {
                const newRecipienPhone = { ...response };
                recipientPhone.current = ({ ...newRecipienPhone });
                setHasPhone(true);
                setShowPhoneMissingModal(false);
            } else {
                setHasPhone(false);
                if (hasPhoneVerification && (currentStep !== 0 || newEmail.current)) {
                    await handlePhoneMissing();
                }
            }
                } catch (error) {
                    console.error('Error checking phone availability:', error);
                    setHasPhone(false);
                    if (hasPhoneVerification && (currentStep !== 0 || newEmail.current)) {
                        await handlePhoneMissing();
                    }
                } finally {
                    updateRequestsMap('checkPhoneAvailability', false);
                }
    };

    const sendAdminMFACode = async (method: AdminMFAMethod): Promise<void> => {
        try {
            const queryParams = new URLSearchParams(window.location.search);
            const linkId = caseInsensitiveUrlParam(queryParams, 'linkid');

            await authSettingsStore.API.post(
                BASEURL.backend(),
                ENDPOINTS.sendAuthStep(),
                {
                    response: true,
                    body: { linkId, method },
                },
            );
        } catch (error) {
            console.error('Error sending admin MFA code:', error);
            throw error;
        }
    };

    const handleDidMountedError = async (error) => {
        const { message: errorDescription } = getErrorResponse(error);
        const errorType = parseAuthErrorType(errorDescription);
        if (errorType === 'adminAccessDenied' || errorType === 'collaboratorsAccessDenied') {
            if (typeof window !== 'undefined') {
                if (errorType === 'adminAccessDenied') {
                    sessionStorage.setItem(OTP_ADMINS_RESTRICTED, 'true');
                }
                if (errorType === 'collaboratorsAccessDenied') {
                    sessionStorage.setItem(OTP_ADMINS_RESTRICTED, 'true');
                    sessionStorage.setItem(OTP_COLLABORATORS_RESTRICTED, 'true');
                }
            }
            message.info(i18n.t(`${nameSpace}.OTPSteps.${errorType}`), ADMIN_MESSAGE_DURATION);
            await redirectUnauthenticatedToLogin();
        } else if (errorType === 'signUpForbidden') {
            message.info(i18n.t('codeAuth.firstStepCommon.signUpForbidden'), ADMIN_MESSAGE_DURATION);
        }
    };

    const getPasswordCheckUserIdentifier = (fallbackEmail?: string): string | null => {
        if (isUserLoggedIn) {
            return currentUserEmail;
        }

        return newEmail.current || email || recipientEmail || fallbackEmail || null;
    };

    const checkPasswordAvailability = async (fallbackEmail?: string): Promise<boolean> => {
        // If phone verification is required and we still don't have a phone identity,
        // prioritise phone error and skip password check for now.
        // For logged-in admins / restricted collaborators the phone is fetched best-effort;
        // the backend handles auth via linkId so we must not block the password step here.
        const isAdminMFAFlow = isUserLoggedIn && (isAdmin || (isCollaborator && otpCollaboratorsRestricted));
        if (hasPhoneVerification && !recipientPhone.current?.phone && !isAdminMFAFlow) {
            return false;
        }

        const userIdentifier = getPasswordCheckUserIdentifier(fallbackEmail);

        if (!userIdentifier) {
            return false;
        }

        updateRequestsMap('checkPasswordAvailability', true);
        try {
            const organizationId = localStorage.getItem(CURRENT_ORGANIZATION) || undefined;

            const response = await authSettingsStore.API.get(
                BASEURL.backend(),
                ENDPOINTS.getUserPasswordPublic(userIdentifier, organizationId),
                {},
            );

            // Save hint from response, fallback to config default
            const hint = response?.hint || appConfig.PASSWORD_DEFAULT_HINT || null;
            setPasswordHint(hint);
            sharedFilesStore.setPasswordHint(hint);

            // Initialize MFA session for password verification.
            // All logged-in users use the backend MFA step flow (sendAdminMFACode / verifyAdminMFA)
            // because their Cognito session is already established and calling Auth.signIn again
            // would throw a "User already authenticated" error.
            if (isUserLoggedIn) {
                await sendAdminMFACode('password');
            } else {
                await sendAuthCode(userIdentifier, 'password');
            }

            setHasPassword(true);
            setShowPasswordMissingModal(false);
            return true;
        } catch (error) {
            console.error('Error checking password availability:', error);
            if (!isUserLoggedIn) {
                const { message: errorMessage } = getErrorResponse(error);
                if (errorMessage && parseAuthErrorType(errorMessage)) {
                    await handleDidMountedError(error);
                    return false;
                }
            }
            setHasPassword(false);
            if (hasPasswordProtection) {
                setShowPasswordMissingModal(true);
            }
            return false;
        } finally {
            updateRequestsMap('checkPasswordAvailability', false);
        }
    };

    const getStepIndices = () => {
        let currentIndex = 0;
        const emailStep = currentIndex++;
        const emailVerificationStep = hasEmailVerification ? currentIndex++ : -1;
        const phoneVerificationStep = hasPhoneVerification ? currentIndex++ : -1;
        const passwordStep = hasPasswordProtection ? currentIndex++ : -1;

        return {
            emailStep,
            passwordStep,
            emailVerificationStep,
            phoneVerificationStep,
        };
    };

    const goToPasswordStep = (): void => {
        // Password verification doesn't require sending anything to the user
        // Just transition to the password step where user enters their password
        // Hint is already fetched in checkPasswordAvailability
        setCurrentStep(getStepIndices().passwordStep);
    };

    const checkPhoneAndSendCode = async (): Promise<void> => {
        updateRequestsMap('checkPhoneAndSendCode', true);
        const indices = getStepIndices();
        try {
            const isAdminMFA = isUserLoggedIn && (isAdmin || (isCollaborator && otpCollaboratorsRestricted));
            // sendAdminMFACode uses the linkId (sendAuthStep endpoint) and does not require
            // a phone number fetched via getPhoneByEmail. Skip the guard for admin MFA so
            // the flow can proceed even when the backend blocked getPhoneByEmail with 403.
            if (!isAdminMFA && (!recipientPhone.current?.phone || !recipientPhone.current?.prefix)) {
                throw new Error('Phone number is missing');
            }

            if (isAdminMFA) {
                await sendAdminMFACode('phone');
            } else {
                let emailToUse: string;
                if (isUserLoggedIn && hasEmailVerification && hasPhoneVerification) {
                    emailToUse = currentUserEmail;
                } else {
                    emailToUse = newEmail.current || recipientEmail || currentUserEmail;
                }
                await sendAuthCode(emailToUse, 'phone', recipientPhone.current);
            }
            setCurrentStep(indices.phoneVerificationStep);
        } catch (error) {
            if (isNetworkRestrictionError(error)) {
                handleNetworkRestriction(error);
                return;
            }
            console.error('Error checking phone and sending code:', error);
            const errorMessage = (error as any)?.message || (error as any)?.response?.data?.message || '';
            const isPhoneMissingError = errorMessage.includes('Phone') || errorMessage.includes('phone') || !recipientPhone.current?.phone;

            if (isPhoneMissingError) {
                await handlePhoneMissing();
            }
            await handleDidMountedError(error);
        } finally {
            updateRequestsMap('checkPhoneAndSendCode', false);
        }
    };

    const checkPhoneAndSendVoiceCode = async (): Promise<void> => {
        updateRequestsMap('checkPhoneAndSendVoiceCode', true);
        const indices = getStepIndices();
        try {
            if (!recipientPhone.current?.phone || !recipientPhone.current?.prefix) {
                throw new Error('Phone number is missing');
            }

            if (isUserLoggedIn && (isAdmin || (isCollaborator && otpCollaboratorsRestricted))) {
                await sendAdminMFACode('voice');
            } else {
                let emailToUse: string;
                if (isUserLoggedIn && hasEmailVerification && hasPhoneVerification) {
                    emailToUse = currentUserEmail;
                } else {
                    emailToUse = newEmail.current || recipientEmail || currentUserEmail;
                }
                await sendAuthCode(emailToUse, 'voice', recipientPhone.current);
            }
            setCurrentStep(indices.phoneVerificationStep);
        } catch (error) {
            if (isNetworkRestrictionError(error)) {
                handleNetworkRestriction(error);
                return;
            }
            console.error('Error sending voice code:', error);
            const errorMessage = (error as any)?.message || (error as any)?.response?.data?.message || '';
            const isPhoneMissingError = errorMessage.includes('Phone') || errorMessage.includes('phone') || !recipientPhone.current?.phone;

            if (isPhoneMissingError) {
                await handlePhoneMissing();
            }
            await handleDidMountedError(error);
        } finally {
            updateRequestsMap('checkPhoneAndSendVoiceCode', false);
        }
    };

    const checkPermissionsAfterAuth = async (): Promise<void> => {
        try {
            await userStore.subscribeOnceOnSetUser();
            // File metadata is deferred until the recipient is authenticated;
            // fetch it now (idempotent) before adding permissions.
            await sharedFilesStore.ensureFilesLoaded();
            await sharedFilesStore.tryBatchAddPermissions({ isSilent: false });
        } catch (error) {
            console.error('Error checking permissions after auth:', error);
        }
    };

    type MFARequirement = 'none' | 'phone' | 'password';

    const verifyAdminMFA = async (method: AdminMFAMethod, attempt: string): Promise<void> => {
        const verifyMethod = method === 'password' ? 'password' : 'phone';
        const response = await authSettingsStore.API.post(
            BASEURL.backend(),
            ENDPOINTS.verifyFileAccess(),
            {
                body: {
                    method: verifyMethod,
                    attempt,
                },
            },
        );

        if (!response?.success) {
            throw new Error('MFA verification failed');
        }
    };

    const checkIsMFARequired = async (): Promise<MFARequirement> => {
        // Pre-auth the file list is intentionally empty (metadata is deferred),
        // so there is nothing to probe. The auth-flow-derived phone/password
        // requirements drive the flow instead; skip the file-level probe to
        // avoid issuing a pre-auth file-open request.
        if (!filesList.length) {
            return 'none';
        }
        updateRequestsMap('checkIsMFARequired', true);
        try {
            const filesWithMfa = filesList.find((item) => item.policy.isMfa)?.file_id;
            const filesWithPassword = filesList.find((item) => item?.policy?.settings?.access?.some(
                (setting) => setting.id === REQUIRED_PASSWORD_VERIFICATION && setting.value === true,
            ),
            )?.file_id;
            const body = {
                app_name: OpenOptions.viewer,
                always_check_mfa: true,
            };

            const result = await authSettingsStore.API.post(
                BASEURL.backend(),
                ENDPOINTS.openFile(filesWithMfa || filesWithPassword),
                { body },
            );
            if (result) {
                setIsMFARequired(false);
                isMFARequired.current = false;
                return 'none';
            }
        } catch (error) {
            const { statusCode, message: errorMessage } = getErrorResponse(error);
            // Use shared helpers so behavior matches the rest of the app
            if (checkIsMFAPasswordRequiredAPI(statusCode, errorMessage)) {
                setIsMFARequired(true);
                isMFARequired.current = true;
                return 'password';
            }
            if (
                checkIsMFARequiredAPI(statusCode, errorMessage)
                || (hasAllowForwarding && checkMessageContainsPermissions(errorMessage))
            ) {
                setIsMFARequired(true);
                isMFARequired.current = true;
                return 'phone';
            }
            if (checkMessageContainsPermissions(errorMessage)) {
                sharedFilesStore.setErrorType('noPermissions');
                closeOTP();
            }
            return 'none';
        } finally {
            updateRequestsMap('checkIsMFARequired', false);
        }
    };

    useEffect(() => {
        async function fetchData() {
            const initialUserEmail = isUserLoggedIn ? currentUserEmail : recipientEmail;
            const shouldStartEmailFlow = !!initialUserEmail && hasEmailVerification && !isUserLoggedIn;
            const shouldStartPhoneFlow = (!!initialUserEmail || isUserLoggedIn) && hasPhoneVerification;
            const shouldStartPasswordFlow = (!!initialUserEmail || isUserLoggedIn) && hasPasswordProtection;
            const isMfaRequiredByDefault = hasPhoneVerification && !isUserLoggedIn;
            const isPasswordRequiredByDefault = hasPasswordProtection && !isUserLoggedIn;
            const indices = getStepIndices();

            try {
                // 1) No user email known yet → ask for email
                if (!isUserLoggedIn && !initialUserEmail) {
                    setCurrentStep(indices.emailStep);
                    // 2) No email / phone verification configured
                } else if (!shouldStartEmailFlow && !shouldStartPhoneFlow) {
                    // If password protection is enabled and we know the email, go directly to password step
                    if (hasPasswordProtection && initialUserEmail) {
                        const passwordExists = await checkPasswordAvailability(initialUserEmail);
                        if (passwordExists) {
                            await checkIsMFARequired();
                            goToPasswordStep();
                        }
                    } else if (!isUserLoggedIn && !initialUserEmail) {
                        // Only show "enter email" when user is not logged in AND we don’t know recipient email
                        setCurrentStep(indices.emailStep);
                    }
                    // 3) Email verification flow
                } else if (shouldStartEmailFlow) {
                    // If password protection is also enabled, check whether backend expects
                    // only password MFA and skip email verification in that case.
                    if (hasPasswordProtection) {
                        const mfaRequirement = await checkIsMFARequired();
                        if (mfaRequirement === 'password') {
                            const passwordExists = await checkPasswordAvailability(initialUserEmail);
                            if (passwordExists) {
                                goToPasswordStep();
                                return;
                            }
                        }
                    }
                    // When AUTOMATIC_OTP is false, show email step with pre-filled email
                    // so user can click "Send code" manually
                    if (appConfig.AUTOMATIC_OTP === false) {
                        newEmail.current = initialUserEmail;
                        setCurrentStep(indices.emailStep);
                    } else if (!shouldSkipAutoSend(`email:${initialUserEmail}`)) {
                        if (document.hasFocus()) {
                            await sendEmailAuthCode(initialUserEmail);
                            setCurrentStep(indices.emailVerificationStep);
                        } else {
                            // No human signal — drop to manual click flow.
                            newEmail.current = initialUserEmail;
                            setCurrentStep(indices.emailStep);
                        }
                    } else {
                        newEmail.current = initialUserEmail;
                        setCurrentStep(indices.emailStep);
                    }
                } else if (shouldStartPhoneFlow) {
                    const isAdminMFA = isUserLoggedIn && (isAdmin || (isCollaborator && otpCollaboratorsRestricted));
                    if (isAdminMFA) {
                        try {
                            const phoneResponse = await authSettingsStore.API.get(
                                BASEURL.backend(),
                                ENDPOINTS.getPhoneByEmail(currentUserEmail),
                                {},
                            );
                            if (phoneResponse?.phone && phoneResponse?.prefix) {
                                recipientPhone.current = { phone: phoneResponse.phone, prefix: phoneResponse.prefix };
                                setHasPhone(true);
                            }
                        } catch (phoneErr) {
                            console.warn('Could not fetch admin phone for display:', phoneErr);
                        }

                        if (appConfig.AUTOMATIC_OTP === false) {
                            setIsPhoneCodeSent(false);
                            setCurrentStep(indices.phoneVerificationStep);
                        } else if (!shouldSkipAutoSend(`phone:${initialUserEmail}`)) {
                            if (document.hasFocus()) {
                                await checkPhoneAndSendCode();
                                setIsPhoneCodeSent(true);
                            } else {
                                setIsPhoneCodeSent(false);
                                setCurrentStep(indices.phoneVerificationStep);
                            }
                        } else {
                            setCurrentStep(indices.phoneVerificationStep);
                            setIsPhoneCodeSent(true);
                        }
                    } else {
                        await checkPhoneAvailability();
                        const mfaRequirement = await checkIsMFARequired();
                        if (mfaRequirement === 'phone' || (mfaRequirement === 'none' && isMfaRequiredByDefault)) {
                            const phoneAvailable = !!(recipientPhone.current?.phone && recipientPhone.current?.prefix);
                            if (!phoneAvailable) {
                                await handlePhoneMissing();
                            } else if (appConfig.AUTOMATIC_OTP === false) {
                                setIsPhoneCodeSent(false);
                                setCurrentStep(indices.phoneVerificationStep);
                            } else {
                                await checkPhoneAndSendCode();
                                setIsPhoneCodeSent(true);
                            }
                        } else if (mfaRequirement === 'password' && hasPasswordProtection) {
                            const passwordExists = await checkPasswordAvailability(initialUserEmail);
                            if (passwordExists) {
                                goToPasswordStep();
                                stopLoading();
                            }
                        }
                    }
                } else if (shouldStartPasswordFlow) {
                    const passwordExists = await checkPasswordAvailability(initialUserEmail);
                    if (passwordExists) {
                        if (!isPasswordRequiredByDefault) {
                            await checkIsMFARequired();
                        }
                        setCurrentStep(indices.passwordStep);
                    }
                }
            } catch (error) {
                if (error instanceof InvalidLinkError) {
                    sharedFilesStore.setErrorType('invalidLink');
                    return;
                }
                if (isNetworkRestrictionError(error)) {
                    handleNetworkRestriction(error);
                    return;
                }
                if (!isUserLoggedIn) {
                    await handleDidMountedError(error);
                }
            } finally {
                if (isMainDataLoaded) {
                    stopLoading();
                }
            }
        }
        if (isMainDataLoaded) {
            fetchData();
            stepsMounted();
        }
    }, [isMainDataLoaded]);

    const handleEmailSubmit = async (userEmail: string) => {
        startLoading();
        try {
            newEmail.current = userEmail;
            recipientPhone.current = null;
            setHasPhone(false);

            const isMfaRequiredByDefault = hasPhoneVerification && !isUserLoggedIn;
            const indices = getStepIndices();

            if (hasEmailVerification) {
                if (hasPhoneVerification) {
                    await checkPhoneAvailability();
                }
                if (!shouldSkipAutoSend(`email:${userEmail}`)) {
                    await sendEmailAuthCode(userEmail);
                    if (!anyRequestInProcess) {
                        setCurrentStep(indices.emailVerificationStep);
                    }
                } else {
                    setCurrentStep(indices.emailStep);
                }
            } else if (hasPhoneVerification && !hasEmailVerification) {
                await checkPhoneAvailability();
                // Always check with backend what verification is still needed
                const mfaRequirement = await checkIsMFARequired();
                // If backend says phone is needed, or if we couldn't determine (fallback to policy)
                if (mfaRequirement === 'phone' || (mfaRequirement === 'none' && isMfaRequiredByDefault)) {
                    await checkPhoneAndSendCode();
                    if (!anyRequestInProcess && hasPhone) {
                        setCurrentStep(indices.phoneVerificationStep);
                    }
                } else if (mfaRequirement === 'password' && hasPasswordProtection) {
                    const passwordExists = await checkPasswordAvailability(userEmail);
                    if (passwordExists) {
                        goToPasswordStep();
                        stopLoading();
                    }
                }
            } else if (hasPasswordProtection && !hasPhoneVerification && !hasEmailVerification) {
                const passwordExists = await checkPasswordAvailability(userEmail);
                if (passwordExists) {
                    goToPasswordStep();
                }
            }
        } catch (error) {
            if (error instanceof InvalidLinkError) {
                sharedFilesStore.setErrorType('invalidLink');
                return;
            }
            if (isNetworkRestrictionError(error)) {
                handleNetworkRestriction(error);
                return;
            }
            console.error('Error submitting email:', error);
            const { message: errorMessage } = getErrorResponse(error);
            const restrictedRoleError = parseAuthErrorType(errorMessage);
            await handleDidMountedError(error);
            if (restrictedRoleError !== 'adminAccessDenied' && restrictedRoleError !== 'collaboratorsAccessDenied') {
                onSendEmailError(error);
            }
        } finally {
            stopLoading();
        }
    };

    const handleCodeSubmit = async (code: string) => {
        startLoading();
        const indices = getStepIndices();
        try {
            if (isAdmin && isUserLoggedIn) {
                await verifyAdminMFA('phone', code);
            } else {
                await checkAuthCode(
                    code,
                    () => { updateRequestsMap('checkAuthCode', true); },
                    () => { updateRequestsMap('checkAuthCode', false); },
                    () => { },
                );
            }
            // The recipient is now authenticated (Cognito custom-auth completed).
            // Fetch the deferred file metadata before any file-dependent branch
            // (MFA probe, single/multi-file auto-open) runs.
            await sharedFilesStore.ensureFilesLoaded();
            if (currentStep === indices.emailVerificationStep && hasPhoneVerification) {
                await checkPhoneAvailability();
                // Use ref for immediate check since state updates are async
                const phoneAvailable = !!(recipientPhone.current?.phone && recipientPhone.current?.prefix);
                const mfaRequirement = await checkIsMFARequired();
                if (stepsInitialized) {
                    if (mfaRequirement === 'password' && hasPasswordProtection) {
                        // Password takes precedence if explicitly required by backend
                        const passwordExists = await checkPasswordAvailability();
                        if (passwordExists) {
                            goToPasswordStep();
                            stopLoading();
                        }
                    } else if (phoneAvailable) {
                        // Proceed to phone verification since policy requires it
                        if (appConfig.AUTOMATIC_OTP === false) {
                            // Go to phone step without sending code
                            setIsPhoneCodeSent(false);
                            setCurrentStep(getStepIndices().phoneVerificationStep);
                        } else {
                            // checkPhoneAndSendCode sets the step internally
                            await checkPhoneAndSendCode();
                            setIsPhoneCodeSent(true);
                        }
                    }
                }
            } else if (
                currentStep === indices.emailVerificationStep
                && !hasPhoneVerification
                && hasPasswordProtection
            ) {
                const passwordExists = await checkPasswordAvailability();
                if (passwordExists) {
                    goToPasswordStep();
                    stopLoading();
                }
            } else if (currentStep === indices.phoneVerificationStep && hasPasswordProtection) {
                const passwordExists = await checkPasswordAvailability();
                if (passwordExists) {
                    goToPasswordStep();
                    stopLoading();
                }
            } else if (allFilesCount === 1 && !messageId) {
                setIsMFARequired(false);
                if (newEmail.current && newEmail.current !== recipientEmail) {
                    await checkPermissionsAfterAuth();
                }
                const MAX_AUTO_OPEN_SIZE = 30 * 1024 * 1024;
                let fileTooLarge = false;
                try {
                    const file = filesList[0];
                    if (isOfficeFile(file.filename)) {
                        const fileDetails = await getFile(authSettingsStore.API, file.file_id);
                        if ((fileDetails.file_size ?? 0) > MAX_AUTO_OPEN_SIZE) {
                            fileTooLarge = true;
                        }
                    }
                    if (!fileTooLarge) {
                        selectFile(file.file_id);
                        await openFile(code);
                    }
                } catch {
                    selectFile(filesList[0].file_id);
                    await openFile(code);
                }
                stopLoading();
                if (fileTooLarge) {
                    closeOTP();
                }
            } else {
                setIsMFARequired(false);
                isMFARequired.current = false;
                if (newEmail.current && newEmail.current !== recipientEmail) {
                    await checkPermissionsAfterAuth();
                }
                stopLoading();
                closeOTP();
            }
            setCodeError(false);
        } catch (error) {
            console.error('Error verifying code:', error);
            const { message: errorMessage } = getErrorResponse(error);
            if (errorMessage) {
                const isPermissionError = checkMessageContainsPermissions(errorMessage)
                    || errorMessage.includes('Abort MFA flow')
                    || errorMessage.includes("don't have permission");
                if (isPermissionError) {
                    sharedFilesStore.setErrorType('noPermissions');
                    closeOTP();
                } else {
                    setCodeError(true);
                }
            } else {
                setCodeError(true);
            }
            stopLoading();
        }
    };

    const handleResendCode = async (method: CodeSendingMethod) => {
        startLoading();
        updateRequestsMap('handleResendCode', true);
        const indices = getStepIndices();
        try {
            if (currentStep === indices.emailVerificationStep) {
                const emailToUse = newEmail.current || recipientEmail;
                if (!shouldSkipAutoSend(`email:${emailToUse}`)) {
                    await sendEmailAuthCode(emailToUse);
                }
            } else if (currentStep === indices.phoneVerificationStep) {
                if (!recipientPhone.current?.phone || !recipientPhone.current?.prefix) {
                    throw new Error('Phone number is missing');
                }
                let emailToUse: string;
                if (isUserLoggedIn && (isAdmin || (isCollaborator && otpCollaboratorsRestricted))) {
                    await sendAdminMFACode(method);
                } else {
                    if (isUserLoggedIn && hasEmailVerification && hasPhoneVerification) {
                        emailToUse = currentUserEmail;
                    } else {
                        emailToUse = newEmail.current || recipientEmail;
                    }
                    if (!shouldSkipAutoSend(`phone:${emailToUse}`)) {
                        await sendAuthCode(emailToUse, method, recipientPhone.current);
                    }
                }
            }
        } catch (error) {
            if (isNetworkRestrictionError(error)) {
                handleNetworkRestriction(error);
                return;
            }
            console.error('Error resending code:', error);
            if (currentStep === indices.emailVerificationStep) {
                onSendEmailError(error);
            } else if (currentStep === 2) {
                const errorMessage = (error as any)?.message || (error as any)?.response?.data?.message || '';
                const isPhoneMissingError = errorMessage.includes('Phone') || errorMessage.includes('phone') || !recipientPhone.current?.phone;

                if (isPhoneMissingError) {
                    await handlePhoneMissing();
                } else {
                    message.error(i18n.t(`${nameSpace}.OTPSteps.errorResendingCode`));
                }
            }
        } finally {
            stopLoading();
            updateRequestsMap('handleResendCode', false);
        }
    };

    const handlePasswordSubmit = async (password: string) => {
        if (!wrongPasswordAttempts) {
            return;
        }

        startLoading();
        const initialUserEmail = isUserLoggedIn ? currentUserEmail : recipientEmail;
        try {
            if (isUserLoggedIn) {
                // All logged-in users verify via the backend MFA step endpoint, because
                // signedInUserRef is not populated for already-authenticated sessions.
                await verifyAdminMFA('password', password);
            } else {
                await checkAuthCode(
                    password,
                    () => { updateRequestsMap('checkAuthCode', true); },
                    () => { updateRequestsMap('checkAuthCode', false); },
                    () => { },
                );
            }
            setIsMFARequired(false);
            isMFARequired.current = false;
            // Authenticated via password: fetch the deferred file metadata
            // before the single/multi-file auto-open branch reads filesList.
            await sharedFilesStore.ensureFilesLoaded();
            if (newEmail.current && newEmail.current !== recipientEmail) {
                await checkPermissionsAfterAuth();
            }
            if (allFilesCount === 1 && !messageId) {
                const MAX_AUTO_OPEN_SIZE = 30 * 1024 * 1024;
                let fileTooLarge = false;
                try {
                    const file = filesList[0];
                    if (isOfficeFile(file.filename)) {
                        const fileDetails = await getFile(authSettingsStore.API, file.file_id);
                        if ((fileDetails.file_size ?? 0) > MAX_AUTO_OPEN_SIZE) {
                            fileTooLarge = true;
                        }
                    }
                    if (!fileTooLarge) {
                        selectFile(file.file_id);
                        await openFile(password);
                    }
                } catch {
                    selectFile(filesList[0].file_id);
                    await openFile(password);
                }
                stopLoading();
                if (fileTooLarge) {
                    closeOTP();
                }
            } else {
                stopLoading();
                closeOTP();
            }
            setPasswordError(false);
            setWrongPasswordAttempts(3);
        } catch (error) {
            console.error('Error in password submit:', error);
            const { message: errorMessage } = getErrorResponse(error);
            // For external users, route admin/collaborator OTP restriction errors
            // through the shared handler so behavior matches phone/email flows.
            if (!isUserLoggedIn && errorMessage && parseAuthErrorType(errorMessage)) {
                await handleDidMountedError(error);
                stopLoading();
                return;
            }
            if (errorMessage) {
                const isPermissionError = checkMessageContainsPermissions(errorMessage)
                    || errorMessage.includes('Abort MFA flow')
                    || errorMessage.includes("don't have permission");
                if (isPermissionError) {
                    sharedFilesStore.setErrorType('noPermissions');
                    closeOTP();
                } else {
                    setPasswordError(true);
                    setWrongPasswordAttempts((prev) => (prev > 0 ? prev - 1 : 0));
                }
            } else {
                setPasswordError(true);
                setWrongPasswordAttempts((prev) => (prev > 0 ? prev - 1 : 0));
            }
            stopLoading();
        }
    };

    const stepsVisible = (): boolean => {
        const ifEmailExist = !!recipientEmail;
        // Show steps if we have password protection OR if email/phone verification match
        const hasMultipleSteps = hasPasswordProtection
            || hasEmailVerification
            || hasPhoneVerification;

        // Original logic: hide steps if email exists and only one verification type is enabled (no password)
        const shouldHideForSingleVerification = ifEmailExist
            && hasEmailVerification !== hasPhoneVerification
            && !hasPasswordProtection;

        return hasMultipleSteps && !shouldHideForSingleVerification;
    };

    const stepIndices = getStepIndices();

    const steps = [
        { title: t(`${nameSpace}.OTPSteps.enterEmail`) },
        ...(hasEmailVerification ? [{ title: t(`${nameSpace}.OTPSteps.verifyEmail`) }] : []),
        ...(hasPhoneVerification ? [{ title: t(`${nameSpace}.OTPSteps.verifyPhone`) }] : []),
        ...(hasPasswordProtection ? [{ title: t(`${nameSpace}.OTPSteps.verifyPassword`) }] : []),
    ];
    const stepsDisplay = stepsVisible() && currentStep !== null;

    return (
        <div className={styles['share-with-external']} data-testid="recipientPage_auth_container">
            {stepsDisplay && (
                <Steps current={currentStep} labelPlacement="vertical" className={styles.stepsContainer} data-testid="recipientPage_auth_steps">
                    {steps.map((step, index) => (
                        <Step key={index} title={step.title} />
                    ))}
                </Steps>
            )}
            {currentStep === null && !(isLoading || anyRequestInProcess) && (<Spinner spinning style={{ margin: 'auto', width: '100%' }} data-testid="recipientPage_auth_spinner" />)}
            {currentStep === stepIndices.emailStep && (
                <OTPAuthEmailBox
                    recipientEmail={newEmail.current || recipientEmail}
                    onEmailSubmit={handleEmailSubmit}
                    isLoading={isLoading || anyRequestInProcess}
                    errorMessages={emailAuthFirstStepMessages}
                    textSource={textSource}
                    data-testid="recipientPage_auth_emailBox"
                />
            )}
            {currentStep === stepIndices.passwordStep && hasPasswordProtection && (
                <>
                    <OTPAuthPasswordBox
                        onPasswordSubmit={handlePasswordSubmit}
                        isLoading={isLoading}
                        passwordError={passwordError}
                        setPasswordError={setPasswordError}
                        wrongPasswordAttempts={wrongPasswordAttempts}
                        hint={passwordHint}
                        data-testid="recipientPage_auth_passwordBox"
                    />
                    {!isUserLoggedIn && (
                        <EmailChangeMessage
                            recipientEmail={newEmail.current || recipientEmail}
                            onEditEmail={() => {
                                setCurrentStep(stepIndices.emailStep);
                                newEmail.current = '';
                                setPasswordError(false);
                            }}
                            data-testid="recipientPage_auth_emailChangeMessage_password"
                        />
                    )}
                </>
            )}
            {currentStep === stepIndices.emailVerificationStep && hasEmailVerification && (
                <OTPAuthCodeBox
                    recipientEmail={newEmail.current || email || recipientEmail}
                    onCodeSubmit={handleCodeSubmit}
                    onResendCode={handleResendCode}
                    onEditEmail={() => {
                        setCurrentStep(stepIndices.emailStep);
                        setTextSource('email');
                        newEmail.current = '';
                        recipientPhone.current = null;
                        setHasPhone(false);
                        resetEmail();
                    }}
                    errorMessages={emailAuthFirstStepMessages}
                    codeError={codeError}
                    setCodeError={setCodeError}
                    data-testid="recipientPage_auth_codeBox_email"
                />
            )}
            {currentStep === stepIndices.phoneVerificationStep && hasPhoneVerification && (
                <>
                    {!isPhoneCodeSent && appConfig.AUTOMATIC_OTP === false ? (
                        <OTPAuthPhoneBox
                            phoneNumber={`+${recipientPhone.current?.prefix}${recipientPhone.current?.phone}`}
                            onSendCode={async () => {
                                await checkPhoneAndSendCode();
                                setIsPhoneCodeSent(true);
                            }}
                            onSendVoiceCode={async () => {
                                await checkPhoneAndSendVoiceCode();
                                setIsPhoneCodeSent(true);
                            }}
                            isLoading={isLoading || anyRequestInProcess}
                            data-testid="recipientPage_auth_phoneBox"
                        />
                    ) : (
                        <>
                            <OTPAuthCodeBox
                                recipientEmail={`+${recipientPhone.current?.prefix}${recipientPhone.current?.phone}`}
                                onCodeSubmit={handleCodeSubmit}
                                onResendCode={handleResendCode}
                                onEditEmail={() => {
                                    setCurrentStep(stepIndices.emailStep);
                                    setTextSource('phone');
                                    newEmail.current = '';
                                    recipientPhone.current = null;
                                    setHasPhone(false);
                                    resetEmail();
                                }}
                                codeError={codeError}
                                setCodeError={setCodeError}
                                isPhoneVerification
                                data-testid="recipientPage_auth_codeBox_phone"
                            />
                            {!hasEmailVerification && !isUserLoggedIn && (
                                <EmailChangeMessage
                                    recipientEmail={newEmail.current || recipientEmail}
                                    onEditEmail={() => {
                                        setCurrentStep(stepIndices.emailStep);
                                        setTextSource('phone');
                                        newEmail.current = '';
                                        recipientPhone.current = null;
                                        setHasPhone(false);
                                        resetEmail();
                                    }}
                                    data-testid="recipientPage_auth_emailChangeMessage"
                                />
                            )}
                        </>
                    )}
                </>
            )}
            <PhoneMissingModal
                isOpen={showPhoneMissingModal}
                onCancel={() => {
                    setShowPhoneMissingModal(false);
                }}
                senderEmail={senderEmail}
                data-testid="recipientPage_auth_phoneMissingModal"
            />
            <PasswordMissingModal
                isOpen={showPasswordMissingModal}
                onCancel={() => {
                    setShowPasswordMissingModal(false);
                }}
                senderEmail={senderEmail}
                data-testid="recipientPage_auth_passwordMissingModal"
            />
        </div>
    );
});

export default OTPAuthSteps;
